import React from 'react';
import './Certificate.css';
import CertificateBorder from './CertificateBorder';
import CertificateSeal from './CertificateSeal';
import CertificateQRCode from './CertificateQRCode';

const Certificate = ({ 
  studentName, 
  courseName, 
  issueDate, 
  certificateId,
  founderName = "Mukesh Gautam",
  hrName = "Vandana Lokhman",
  startDate,
  endDate
}) => {
  const startFormatted = startDate 
    ? new Date(startDate).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })
    : 'June 01, 2026';
    
  const endFormatted = endDate 
    ? new Date(endDate).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })
    : 'July 15, 2026';

  return (
    <div id="certificate-download-area" className="certificate-container flex-shrink-0 mx-auto bg-white">
      {/* Abstract SVGs Borders and Backgrounds */}
      <div className="certificate-waves"></div>
      <CertificateBorder />

      {/* Main Content Layout Grid */}
      <div className="absolute inset-0 z-20 flex flex-col justify-between px-24 py-16 text-center">
        
        {/* Top Bar: Company Name (Left) & Cert Details (Right) */}
        <div className="w-full flex justify-between items-start mb-6 px-4">
          {/* Company Name (Left) */}
          <div className="flex items-center gap-4">
             <div className="w-14 h-14 flex items-center justify-center">
                <img src="/cc.png" alt="CCAI Logo" className="w-full h-full object-contain" onError={(e) => { e.target.style.display = 'none'; }} />
             </div>
             <div className="text-left">
               <div className="text-[#6D4CFF] text-[24px] font-bold tracking-[0.1em] leading-none mb-1">
                 CLIENTCODE AI
               </div>
               <div className="text-[#1A2340] text-[10px] tracking-[0.25em] font-medium opacity-70">
                 INNOVATION PVT. LTD.
               </div>
             </div>
          </div>
          
          {/* Cert Details (Right) */}
          <div className="flex flex-col text-right text-[12px] text-[#1A2340] opacity-90">
            <div className="mb-1.5"><strong className="mr-2 text-[#6D4CFF]">Certificate ID:</strong> {certificateId || "CCAI-INT-2026-P9A5"}</div>
          </div>
        </div>

        {/* Header Region */}
        <div className="flex flex-col items-center mt-2">
          <h1 
            className="text-[64px] font-[800] tracking-tight leading-none mb-2" 
            style={{ 
              backgroundImage: 'linear-gradient(90deg, #6D4CFF, #2D8CFF)', 
              WebkitBackgroundClip: 'text', 
              WebkitTextFillColor: 'transparent',
              color: 'transparent'
            }}
          >
            CERTIFICATE
          </h1>
          <div className="text-[#1A2340] text-[24px] tracking-[0.15em] font-bold uppercase mb-8">
            OF COMPLETION
          </div>
          
          <div className="text-[#1A2340] text-[14px] tracking-[0.2em] font-medium uppercase opacity-80 flex items-center w-full justify-center">
            <div className="h-[1px] w-20 bg-[#1A2340] opacity-30 mr-6"></div>
            THIS IS TO CERTIFY THAT
            <div className="h-[1px] w-20 bg-[#1A2340] opacity-30 ml-6"></div>
          </div>
        </div>

        {/* Student Name Region */}
        <div className="flex flex-col items-center justify-center my-4">
          <h2 className="certificate-name text-[72px] text-[#1A2340] leading-tight px-12 pb-2 border-b-[1.5px] border-[#6D4CFF] border-opacity-40 min-w-[600px]">
            {studentName || "Pankaj Ram"}
          </h2>
        </div>

        {/* Description Region */}
        <div className="flex flex-col items-center px-16 mb-4">
          <p className="text-[#1A2340] text-[18px] mb-4">
            Has successfully completed the Internship Program in
          </p>
          <h3 className="text-[#6D4CFF] text-[32px] font-bold uppercase tracking-wider mb-2">
            {courseName || "BACKEND DEVELOPMENT"}
          </h3>
          <p className="text-[#1A2340] text-[16px] font-medium mb-6">
            from <span className="text-[#6D4CFF] font-bold">{startFormatted}</span> to <span className="text-[#6D4CFF] font-bold">{endFormatted}</span>
          </p>
          <p className="text-[#1A2340] opacity-80 text-[14px] max-w-[800px] leading-relaxed px-10">
            For demonstrating exceptional dedication, skill, and commitment throughout the duration of the internship.
          </p>
        </div>

        {/* Footer Layout (Signatures & Seal, QR) */}
        <div className="w-full flex justify-between items-end mt-auto px-8 pb-4 z-30">
          
          {/* Empty left spacer since Date moved to top */}
          <div className="w-48"></div>

          {/* Signatures & Seal Center Block */}
          <div className="flex-1 flex justify-center items-end px-2 gap-8">
            {/* Left Signature */}
            <div className="flex flex-col items-center justify-end">
              <div className="certificate-name text-[46px] text-[#1A2340] opacity-80 mb-2 transform -rotate-3 whitespace-nowrap">
                {founderName}
              </div>
              <div className="w-48 h-[1px] bg-[#E5E7EB] mb-2"></div>
              <div className="text-[#6D4CFF] font-bold text-[11px] tracking-widest uppercase mt-1 whitespace-nowrap">Founder & CEO</div>
            </div>

            {/* Center Seal */}
            <div className="flex justify-center pb-6 px-4">
              <CertificateSeal />
            </div>

            {/* Right Signature */}
            <div className="flex flex-col items-center justify-end">
              <div className="certificate-name text-[46px] text-[#1A2340] opacity-80 mb-2 transform -rotate-2 whitespace-nowrap">
                {hrName}
              </div>
              <div className="w-48 h-[1px] bg-[#E5E7EB] mb-2"></div>
              <div className="text-[#6D4CFF] font-bold text-[11px] tracking-widest uppercase mt-1 whitespace-nowrap">HR Manager</div>
            </div>
          </div>

          {/* QR Code (Bottom Right) */}
          <div className="w-48 flex justify-end mb-1">
             <CertificateQRCode certificateId={certificateId} />
          </div>

        </div>

      </div>
    </div>
  );
};

export default Certificate;
